// Apps-Tab der Admin-Konsole — die drei Mednos-Apps mit ihren Wartungsvorgaengen.
//
// Aufklappbare Liste statt drei nebeneinanderliegender Bereiche: die meisten Apps
// haben heute gar keinen Vorgang, und eine flache Liste mit zwei leeren Bloecken
// liest sich wie ein Fehler. Wer aufklappt, sieht entweder Knoepfe oder einen Satz,
// warum hier nichts steht.

// Kein authApi hier: dieser Endpunkt liegt auf Papers selbst (gleiche Origin),
// nicht am Auth-Server auf mednos.eu.
import { ppButton, ppHint, ppLabel, ppLink } from './platform-api.js';
import { initZbotUnlockOverlay } from './zbot-unlock-overlay.js';
import { akkordeonEintrag } from './admin-akkordeon.js';

export const APPS_CSS = `
/* Kopfzeile eines Abschnitts: Ueberschrift links, Aktion rechts. */
.mednos-quellen__kopf { display: flex; align-items: center; gap: 12px; }
/* ppLabel, ppLink und ppButton bringen eigene align-self-Regeln mit
   (flex-start) und haengen in einer Zeile sonst auf drei Hoehen. */
.mednos-quellen__kopf .profile-panel__link,
.mednos-quellen__kopf .profile-panel__btn { align-self: center; }
.mednos-quellen__kopf .profile-panel__btn { margin-left: auto; }
.mednos-quellen__zeile {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 10px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}
.mednos-quellen__punkt {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
/* Gruen nur, wenn die Quelle wirklich benutzbar ist. Eine Sicherheitsabfrage ist
   erreichbar, aber verschlossen - sie faerbt deshalb rot, denn genau sie ist der
   Grund, den Freischalt-Knopf zu druecken. Der Text daneben sagt, welcher der
   beiden Faelle vorliegt; Farbe allein traegt die Aussage nicht. */
.mednos-quellen__punkt--online { background: #199e70; }
.mednos-quellen__punkt--gesperrt { background: #d95926; }
.mednos-quellen__punkt--offline { background: #e66767; }
.mednos-quellen__name {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.76rem;
}
.mednos-quellen__hinweis { margin-left: auto; font-size: 0.72rem; color: #b0b0b0; }
.mednos-quellen__aktion { flex-shrink: 0; }
.mednos-quellen__trenner { height: 1px; background: rgba(255, 255, 255, 0.1); margin: 4px 0; }
`;

/**
 * Eine Quellenzeile: Ampelpunkt, Adresse, Zustand im Klartext — und bei einer
 * klemmenden Schattenquelle ein eigener Freischalt-Knopf.
 *
 * Der Knopf oben in der Kopfzeile nimmt sich alle vor; dieser hier genau diese
 * eine. Beides ist sinnvoll: meist klemmt eine einzelne, und dann will man nicht
 * durch die uebrigen blaettern.
 */
function quellenZeile(q, freischalten) {
  const zeile = document.createElement('div');
  zeile.className = 'mednos-quellen__zeile';
  const punkt = document.createElement('span');
  const art = ['online', 'gesperrt', 'offline'].includes(q.status) ? q.status : 'offline';
  punkt.className = `mednos-quellen__punkt mednos-quellen__punkt--${art}`;
  const name = document.createElement('span');
  name.className = 'mednos-quellen__name';
  name.textContent = q.url;
  const hinweis = document.createElement('span');
  hinweis.className = 'mednos-quellen__hinweis';
  hinweis.textContent = q.hinweis || art;
  zeile.append(punkt, name, hinweis);

  // Nur wo ein geloestes Captcha ueberhaupt hilft: eine freie API kennt keine
  // Sicherheitsabfrage, eine antwortende Quelle braucht nichts, und einer toten
  // Quelle ist mit einem Cookie nicht beizukommen.
  if (q.freischaltbar && art === 'gesperrt' && freischalten) {
    const knopf = ppLink('Freischalten', () => freischalten(q.url));
    knopf.classList.add('mednos-quellen__aktion');
    zeile.append(knopf);
  }
  return zeile;
}

/**
 * Baut die Vorgaenge fuer Papers: die Quellenliste mit Zustand und daneben den
 * Freischalt-Knopf, der fuer ALLE Quellen gilt.
 *
 * Die Live-Freischaltung ist an `papers.mednos.eu` gebunden: die zbot-Endpunkte
 * haengen ausschliesslich an Papers, ein Aufruf von study/radiology aus haette kein
 * Ziel (kein Cross-Origin-Setup dafuer vorgesehen, s. Spec 2026-08-23).
 */
/**
 * Der institutionelle Zugang als eigene Zeile.
 *
 * Eigener Block, nicht eine Quelle unter vielen: er wird erst gefragt, wenn ALLE
 * Quellen versagt haben, und er wird anders behandelt — die Anmeldung haelt nur,
 * solange der Browser laeuft, deshalb bleibt er nach 'Fertig' offen.
 */
function shibZeile(shib, anmelden) {
  const zeile = document.createElement('div');
  zeile.className = 'mednos-quellen__zeile';

  const punkt = document.createElement('span');
  const art = !shib?.aktiv ? 'offline' : (shib.angemeldet ? 'online' : 'gesperrt');
  punkt.className = `mednos-quellen__punkt mednos-quellen__punkt--${art}`;

  const name = document.createElement('span');
  name.className = 'mednos-quellen__name';
  name.textContent = 'Institutioneller Zugang';

  const hinweis = document.createElement('span');
  hinweis.className = 'mednos-quellen__hinweis';
  hinweis.textContent = !shib?.aktiv
    ? 'deaktiviert (SHIB_ENABLED)'
    : (shib.angemeldet ? 'angemeldet' : 'nicht angemeldet');

  zeile.append(punkt, name, hinweis);

  // Anmelden nur, wenn er eingeschaltet ist UND ein Einstieg konfiguriert wurde -
  // ohne Adresse gaebe es nichts zu oeffnen.
  if (shib?.aktiv && shib.einstieg && !shib.angemeldet) {
    const knopf = ppLink('Anmelden', () => anmelden());
    knopf.classList.add('mednos-quellen__aktion');
    zeile.append(knopf);
  }
  return zeile;
}

/** Unix-Sekunden als lesbare Ortszeit. 0 heisst: noch nie geprueft. */
function zeitpunkt(sekunden) {
  if (!sekunden) return 'noch nie';
  return new Date(sekunden * 1000).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Merkt sich, dass der Papers-Eintrag aufgeklappt sein soll.
 *
 * Nach dem Freischalten kehrt man in den Apps-Tab zurueck; ihn dort wieder
 * zugeklappt vorzufinden hiesse, sich erneut durchzuklicken. Modulweit statt im
 * Aufruf, weil der Tab beim Zurueckkommen komplett neu gebaut wird.
 */
let papersOffen = false;

/**
 * Nach dem Freischalten wird beim Zurueckkommen NEU geprueft, nicht der gemerkte
 * Befund gezeigt. Sonst stuende dort weiter „Sicherheitsabfrage steht", obwohl man
 * sie gerade geloest hat — der Knopf saehe wirkungslos aus.
 */
let frischPruefen = false;

function papersVorgaenge(konsole) {
  const kopf = document.createElement('div');
  kopf.className = 'mednos-quellen__kopf';
  kopf.append(ppLabel('Quellen'));

  const liste = document.createElement('div');
  liste.className = 'mednos-admin__section';
  liste.append(ppHint('Prüfe Quellen…'));

  if (location.hostname === 'papers.mednos.eu') {
    let overlay = null;
    const oeffneSitzung = (pfad) => {
      if (!overlay) overlay = initZbotUnlockOverlay();
      papersOffen = true;
      frischPruefen = true;
      konsole.close();
      // Nach "Fertig" zurueck in den Apps-Tab, Papers wieder aufgeklappt.
      overlay.oeffnenMitPfad(pfad, () => konsole.open('apps'));
    };
    const freischalten = (quelle) => oeffneSitzung(
      quelle
        ? `/api/admin/zbot-unlock/start?quelle=${encodeURIComponent(quelle)}`
        : '/api/admin/zbot-unlock/start',
    );
    const anmelden = () => oeffneSitzung('/api/admin/zbot-unlock/start?ziel=shib');

    kopf.append(ppLink('Aktualisieren', () => lade(true)));
    // Ohne Argument: der Server nimmt sich alle klemmenden Quellen vor.
    kopf.append(ppButton('Alle freischalten', () => freischalten()));

    /**
     * Holt den Befund ueber Papers' Admin-Proxy; zbot selbst bleibt intern.
     * `frisch` erzwingt eine neue Pruefung — sonst kommt der letzte Befund
     * zurueck, damit ein Aufklappen nicht dreizehn fremde Server anfasst.
     */
    function lade(frisch) {
      liste.replaceChildren(ppHint(frisch ? 'Prüfe Quellen…' : 'Lade…'));
      const pfad = frisch
        ? '/api/admin/zbot-unlock/quellen?frisch=1'
        : '/api/admin/zbot-unlock/quellen';
      fetch(pfad, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => {
          const quellen = d.quellen || [];
          const teile = quellen.length
            ? quellen.map((q) => quellenZeile(q, freischalten))
            : [ppHint('Keine Quelle konfiguriert.')];
          const trenner = document.createElement('div');
          trenner.className = 'mednos-quellen__trenner';
          teile.push(trenner, shibZeile(d.shib, anmelden));
          teile.push(ppHint(`Zuletzt geprüft: ${zeitpunkt(d.geprueft_am)}`));
          liste.replaceChildren(...teile);
        })
        .catch(() => liste.replaceChildren(ppHint('Zustand der Quellen nicht abrufbar.')));
    }
    lade(frischPruefen);
    frischPruefen = false;
  } else {
    liste.replaceChildren(ppHint(
      'Die Quellen und die Freischaltung sind nur auf papers.mednos.eu erreichbar — '
      + 'zbot hängt an Papers.',
    ));
  }

  return [kopf, liste];
}

/**
 * Baut den Apps-Tab in `container`.
 *
 * @param {HTMLElement} container Zielelement
 * @param {{open: (tab?: string) => void, close: () => void}} konsole Die Konsole
 *   selbst: schliessen, weil das Freischalt-Overlay die volle Flaeche braucht, und
 *   wieder oeffnen, wenn man von dort zurueckkommt.
 */
export function renderAppsPane(container, konsole) {
  const leer = () => [ppHint('Für diese App gibt es derzeit keinen Wartungsvorgang.')];
  container.replaceChildren(
    akkordeonEintrag('Papers', 'papers.mednos.eu', papersVorgaenge(konsole), papersOffen),
    akkordeonEintrag('Study', 'study.mednos.eu', leer()),
    akkordeonEintrag('Radiology', 'mednos.eu', leer()),
  );
  papersOffen = false;
}
